/**
 * Get user's IP address for SMS rate limiting
 * Uses multiple fallback services to ensure reliability
 */
export async function getUserIpAddress() {
  try {
    // Try ipify first (most reliable)
    const response = await fetch('https://api.ipify.org?format=json');
    if (response.ok) {
      const data = await response.json();
      if (data.ip) return data.ip;
    }
  } catch (error) {
    console.warn('Failed to get IP from ipify:', error);
  }

  try {
    // Fallback to ipapi
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.ip) return data.ip;
    }
  } catch (error) {
    console.warn('Failed to get IP from ipapi:', error);
  }

  try {
    // Fallback to ip-api
    const response = await fetch('https://ip-api.com/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.query) return data.query;
    }
  } catch (error) {
    console.warn('Failed to get IP from ip-api:', error);
  }

  // If all services fail, return a fallback
  console.error('All IP detection services failed');
  return 'unknown';
}

// Convert 2-letter country code to full name, fallback to code if not found
function getCountryName(code) {
  const map = {
    AF: "Afghanistan", AX: "Åland Islands", AL: "Albania", DZ: "Algeria",
    AS: "American Samoa", AD: "Andorra", AO: "Angola", AI: "Anguilla",
    AQ: "Antarctica", AG: "Antigua and Barbuda", AR: "Argentina", AM: "Armenia",
    AW: "Aruba", AU: "Australia", AT: "Austria", AZ: "Azerbaijan",
    BS: "Bahamas", BH: "Bahrain", BD: "Bangladesh", BB: "Barbados",
    BY: "Belarus", BE: "Belgium", BZ: "Belize", BJ: "Benin",
    BM: "Bermuda", BT: "Bhutan", BO: "Bolivia", BA: "Bosnia and Herzegovina",
    BW: "Botswana", BV: "Bouvet Island", BR: "Brazil", IO: "British Indian Ocean Territory",
    BN: "Brunei Darussalam", BG: "Bulgaria", BF: "Burkina Faso", BI: "Burundi",
    KH: "Cambodia", CM: "Cameroon", CA: "Canada", CV: "Cape Verde",
    KY: "Cayman Islands", CF: "Central African Republic", TD: "Chad", CL: "Chile",
    CN: "China", CX: "Christmas Island", CC: "Cocos (Keeling) Islands", CO: "Colombia",
    KM: "Comoros", CG: "Congo", CD: "Congo, Democratic Republic of the", CK: "Cook Islands",
    CR: "Costa Rica", CI: "Côte d'Ivoire", HR: "Croatia", CU: "Cuba",
    CY: "Cyprus", CZ: "Czech Republic", DK: "Denmark", DJ: "Djibouti",
    DM: "Dominica", DO: "Dominican Republic", EC: "Ecuador", EG: "Egypt",
    SV: "El Salvador", GQ: "Equatorial Guinea", ER: "Eritrea", EE: "Estonia",
    ET: "Ethiopia", FK: "Falkland Islands", FO: "Faroe Islands", FJ: "Fiji",
    FI: "Finland", FR: "France", GF: "French Guiana", PF: "French Polynesia",
    TF: "French Southern Territories", GA: "Gabon", GM: "Gambia", GE: "Georgia",
    DE: "Germany", GH: "Ghana", GI: "Gibraltar", GR: "Greece",
    GL: "Greenland", GD: "Grenada", GP: "Guadeloupe", GU: "Guam",
    GT: "Guatemala", GG: "Guernsey", GN: "Guinea", GW: "Guinea-Bissau",
    GY: "Guyana", HT: "Haiti", HM: "Heard Island and McDonald Islands", VA: "Holy See",
    HN: "Honduras", HK: "Hong Kong", HU: "Hungary", IS: "Iceland",
    IN: "India", ID: "Indonesia", IR: "Iran", IQ: "Iraq",
    IE: "Ireland", IM: "Isle of Man", IL: "Israel", IT: "Italy",
    JM: "Jamaica", JP: "Japan", JE: "Jersey", JO: "Jordan",
    KZ: "Kazakhstan", KE: "Kenya", KI: "Kiribati", KP: "North Korea",
    KR: "South Korea", KW: "Kuwait", KG: "Kyrgyzstan", LA: "Laos",
    LV: "Latvia", LB: "Lebanon", LS: "Lesotho", LR: "Liberia",
    LY: "Libya", LI: "Liechtenstein", LT: "Lithuania", LU: "Luxembourg",
    MO: "Macao", MK: "North Macedonia", MG: "Madagascar", MW: "Malawi",
    MY: "Malaysia", MV: "Maldives", ML: "Mali", MT: "Malta",
    MH: "Marshall Islands", MQ: "Martinique", MR: "Mauritania", MU: "Mauritius",
    YT: "Mayotte", MX: "Mexico", FM: "Micronesia", MD: "Moldova",
    MC: "Monaco", MN: "Mongolia", MS: "Montserrat", MA: "Morocco",
    MZ: "Mozambique", MM: "Myanmar", NA: "Namibia", NR: "Nauru",
    NP: "Nepal", NL: "Netherlands", NC: "New Caledonia", NZ: "New Zealand",
    NI: "Nicaragua", NE: "Niger", NG: "Nigeria", NU: "Niue",
    NF: "Norfolk Island", MP: "Northern Mariana Islands", NO: "Norway", OM: "Oman",
    PK: "Pakistan", PW: "Palau", PS: "Palestine", PA: "Panama",
    PG: "Papua New Guinea", PY: "Paraguay", PE: "Peru", PH: "Philippines",
    PN: "Pitcairn", PL: "Poland", PT: "Portugal", PR: "Puerto Rico",
    QA: "Qatar", RE: "Réunion", RO: "Romania", RU: "Russia",
    RW: "Rwanda", BL: "Saint Barthélemy", SH: "Saint Helena", KN: "Saint Kitts and Nevis",
    LC: "Saint Lucia", MF: "Saint Martin", PM: "Saint Pierre and Miquelon", VC: "Saint Vincent and the Grenadines",
    WS: "Samoa", SM: "San Marino", ST: "São Tomé and Príncipe", SA: "Saudi Arabia",
    SN: "Senegal", RS: "Serbia", SC: "Seychelles", SL: "Sierra Leone",
    SG: "Singapore", SK: "Slovakia", SI: "Slovenia", SB: "Solomon Islands",
    SO: "Somalia", ZA: "South Africa", GS: "South Georgia and the South Sandwich Islands", ES: "Spain",
    LK: "Sri Lanka", SD: "Sudan", SR: "Suriname", SJ: "Svalbard and Jan Mayen",
    SZ: "Eswatini", SE: "Sweden", CH: "Switzerland", SY: "Syria",
    TW: "Taiwan", TJ: "Tajikistan", TZ: "Tanzania", TH: "Thailand",
    TL: "Timor-Leste", TG: "Togo", TK: "Tokelau", TO: "Tonga",
    TT: "Trinidad and Tobago", TN: "Tunisia", TR: "Turkey", TM: "Turkmenistan",
    TC: "Turks and Caicos Islands", TV: "Tuvalu", UG: "Uganda", UA: "Ukraine",
    AE: "United Arab Emirates", GB: "United Kingdom", US: "United States", UM: "U.S. Outlying Islands",
    UY: "Uruguay", UZ: "Uzbekistan", VU: "Vanuatu", VE: "Venezuela",
    VN: "Vietnam", VG: "British Virgin Islands", VI: "U.S. Virgin Islands", WF: "Wallis and Futuna",
    EH: "Western Sahara", YE: "Yemen", ZM: "Zambia", ZW: "Zimbabwe"
  };

  return map[code?.toUpperCase()] || code;
}


/**
 * Get user's country name from their IP address
 * Uses multiple fallback services for maximum reliability
 * Returns full country name (e.g., "Sweden") or null if all services fail
 */
export async function getCountryFromIP() {
  // Define services with their field mappings
  // Some return full country names, others return codes - prefer those that return names
  const services = [
    { url: 'https://ipapi.co/json/', field: 'country_name', isPlainText: false },
    { url: 'http://ip-api.com/json/', field: 'country', isPlainText: false },
    { url: 'https://ipinfo.io/json', field: 'country', isPlainText: false, isCode: true },
    { url: 'https://ipwhois.app/json/', field: 'country', isPlainText: false },
    { url: 'https://freeipapi.com/api/json/', field: 'countryName', isPlainText: false },
    { url: 'https://ipdata.co/?api-key=test', field: 'country_name', isPlainText: false },
    { url: 'https://ipapi.co/country_name/', field: null, isPlainText: true },
    { url: 'https://get.geojs.io/v1/ip/country/full', field: null, isPlainText: true },
  ];

  for (const service of services) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(service.url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.ok) {
        if (service.isPlainText) {
          // Plain text response - just trim and return
          const text = await response.text();
          const trimmed = text.trim();
          if (trimmed && trimmed.length > 0 && trimmed.length < 100) {
            return trimmed;
          }
        } else {
          const data = await response.json();
          if (data.error || data.status === 'fail') continue;

          const countryValue = data[service.field];
          if (countryValue && typeof countryValue === 'string') {
            // Convert 2-letter codes to full names, fallback to code if not in map
            return countryValue.length === 2 ? getCountryName(countryValue) : countryValue;
          }
        }
      }
    } catch (error) {
      // Continue to next service
      continue;
    }
  }

  return null;
}
